import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import type { CompanyInfo } from '../api/client'

export type LegalLang = 'tr' | 'en'

// Legal page styles live in web/src/marketing.css (scoped under `.legal`),
// bundled by Vite so they are present on first paint. Fonts load via <link> in
// index.html. This used to be a runtime-injected <style> with an @import, which
// caused an unstyled flash until React mounted.

export type LegalPage = 'terms' | 'privacy' | 'about' | 'distance' | 'delivery' | 'kunye'

const LABELS = {
  tr: {
    privacy: 'Gizlilik Politikası',
    terms: 'Hizmet Şartları',
    about: 'Hakkımızda',
    distance: 'Mesafeli Satış Sözleşmesi',
    delivery: 'Teslimat ve İade',
    kunye: 'Künye',
    signIn: 'Giriş yap',
    switch: 'English',
    footerNote: 'Odoo S.A. ile bağlantılı değildir.',
  },
  en: {
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
    about: 'About Us',
    distance: 'Distance Sales Agreement',
    delivery: 'Delivery and Returns',
    kunye: 'Legal Notice',
    signIn: 'Sign in',
    switch: 'Türkçe',
    footerNote: 'Not affiliated with Odoo S.A.',
  },
} as const

// The path itself carries the language: Turkish pages use Turkish slugs,
// English pages use English slugs. There is no per-language prefix.
export const LEGAL_PATHS = {
  tr: {
    terms: '/hizmet-sartlari',
    privacy: '/gizlilik-politikasi',
    about: '/hakkimizda',
    distance: '/mesafeli-satis-sozlesmesi',
    delivery: '/teslimat-ve-iade',
    kunye: '/kunye',
  },
  en: {
    terms: '/terms',
    privacy: '/privacy',
    about: '/about',
    distance: '/distance-sales-agreement',
    delivery: '/delivery-and-returns',
    kunye: '/legal-notice',
  },
} as const

// Order the footer renders links in. Every legal document is reachable from
// every other one — an acquiring bank's site review checks exactly this.
const FOOTER_ORDER: LegalPage[] = ['about', 'distance', 'delivery', 'privacy', 'terms', 'kunye']

interface LegalShellProps {
  lang: LegalLang
  page: LegalPage
  title: string
  updated: string
  children: ReactNode
}

const APP_NAME = 'Provisyer'

export function LegalShell({ lang, page, title, updated, children }: LegalShellProps) {
  const L = LABELS[lang]
  const altLang: LegalLang = lang === 'en' ? 'tr' : 'en'
  const paths = LEGAL_PATHS[lang]
  const year = new Date().getFullYear()

  return (
    <div className="legal">
      <nav className="legal-nav">
        <Link to="/" className="legal-logo">{APP_NAME}</Link>
        <ul className="legal-nav-links">
          <li><Link to={LEGAL_PATHS[altLang][page]}>{L.switch}</Link></li>
          <li><Link to="/login">{L.signIn}</Link></li>
        </ul>
      </nav>

      <div className="legal-body">
        <h1>{title}</h1>
        <p className="legal-updated">{updated}</p>
        {children}
      </div>

      <footer className="legal-footer">
        <Link to="/" className="legal-footer-logo">{APP_NAME}</Link>
        <ul className="legal-footer-links">
          {FOOTER_ORDER.filter((p) => p !== page).map((p) => (
            <li key={p}><Link to={paths[p]}>{L[p]}</Link></li>
          ))}
        </ul>
        <div className="legal-footer-note">
          © {year} {APP_NAME} - {L.footerNote}
        </div>
      </footer>
    </div>
  )
}

// CompanyBlock renders the seller's legal identity from platform settings.
// Every legal document needs it and none of them should hardcode it, so it
// lives here and reads whatever an admin entered under /platform-settings.
export function CompanyBlock({
  lang,
  company,
}: {
  lang: LegalLang
  company: CompanyInfo
}) {
  const rows: Array<[string, string]> = [
    [lang === 'tr' ? 'Unvan' : 'Legal name', company['company.legal_name']],
    [lang === 'tr' ? 'Adres' : 'Address',
      [company['company.address'], company['company.city']].filter(Boolean).join(', ')],
    [lang === 'tr' ? 'Telefon' : 'Phone', company['company.phone']],
    [lang === 'tr' ? 'E-posta' : 'Email', company['company.email']],
    [lang === 'tr' ? 'Vergi dairesi' : 'Tax office', company['company.tax_office']],
    [lang === 'tr' ? 'Vergi no' : 'Tax number', company['company.tax_no']],
    ['MERSİS', company['company.mersis_no']],
    ['KEP', company['company.kep']],
    [lang === 'tr' ? 'Web sitesi' : 'Website', company['company.website']],
  ]
  const filled = rows.filter(([, v]) => v && v.trim() !== '')
  if (filled.length === 0) return null

  return (
    <table className="legal-table">
      <tbody>
        {filled.map(([label, value]) => (
          <tr key={label}>
            <th scope="row">{label}</th>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
